import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { DollarSign, ShoppingBag, Radio, ExternalLink } from "lucide-react";

/**
 * Surfaces a creator's CashApp + PayPal tip buttons + Services/Products + upcoming Live appearances
 * on their profile detail page.
 */
export default function ProfileExtras({ profile }) {
  const [handles, setHandles] = useState(null);
  const [listings, setListings] = useState([]);
  const [lives, setLives] = useState([]);

  const ownerId = profile?.owner_id;

  useEffect(() => {
    if (!ownerId) return;
    // Public endpoint for handles — fetch from a lightweight user meta endpoint
    api.get(`/users/${ownerId}/payment-handles`).then((r) => setHandles(r.data)).catch(() => setHandles(null));
    api.get(`/listings/user/${ownerId}`).then((r) => setListings(r.data || [])).catch(() => setListings([]));
    api.get(`/live/user/${ownerId}`).then((r) => setLives((r.data || []).filter((l) => l.status !== "ended"))).catch(() => setLives([]));
  }, [ownerId]);

  if (!ownerId) return null;

  const hasHandles = handles?.cashapp_url || handles?.paypal_url;

  if (!hasHandles && listings.length === 0 && lives.length === 0) return null;

  return (
    <div className="mt-10 space-y-8" data-testid="profile-extras">
      {/* Live Now / Upcoming */}
      {lives.length > 0 && (
        <div data-testid="profile-live-block">
          <div className="label text-pfc-gold mb-4 inline-flex items-center gap-2">
            <Radio size={14} className={lives[0].status === "live" ? "text-pfc-red animate-pulse" : ""} />
            {lives[0].status === "live" ? "Live Now" : "Upcoming Live"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {lives.slice(0, 4).map((l) => (
              <div key={l.live_id} className="border border-white/10 bg-ink-800/40 p-4" data-testid={`profile-live-${l.live_id}`}>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] font-sub">
                  <span className={l.status === "live" ? "text-pfc-red" : "text-pfc-gold"}>{l.status === "live" ? "🔴 Live" : l.status}</span>
                  <span className="text-white/40">{l.platform}</span>
                </div>
                <div className="font-sub text-white mt-2">{l.title}</div>
                <div className="text-white/50 text-xs mt-1">{new Date(l.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                {l.stream_url && (
                  <a href={l.stream_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 bg-pfc-gold text-ink-900 text-[11px] uppercase tracking-[0.2em] font-sub hover:bg-pfc-gold-bright">
                    <ExternalLink size={11} /> {l.status === "live" ? "Watch Now" : "Open"}
                  </a>
                )}
              </div>
            ))}
          </div>
          <Link to="/live" className="text-pfc-gold text-xs uppercase tracking-[0.3em] font-sub hover:underline mt-3 inline-block">All Live Appearances →</Link>
        </div>
      )}

      {/* Services & Products */}
      {listings.length > 0 && (
        <div data-testid="profile-listings-block">
          <div className="label text-pfc-gold mb-4 inline-flex items-center gap-2"><ShoppingBag size={14} /> Services &amp; Products</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {listings.map((l) => (
              <ListingTile key={l.listing_id} listing={l} sellerName={profile.name} />
            ))}
          </div>
        </div>
      )}

      {/* Direct Payments */}
      {hasHandles && (
        <div data-testid="profile-payments-block">
          <div className="label text-pfc-gold mb-4 inline-flex items-center gap-2"><DollarSign size={14} /> Tips &amp; Donations</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {handles.cashapp_url && (
              <a href={handles.cashapp_url} target="_blank" rel="noreferrer" className="block px-5 py-4 border border-pfc-gold/30 bg-pfc-gold/5 text-center font-sub uppercase tracking-[0.2em] text-xs text-white hover:bg-pfc-gold hover:text-ink-900 transition" data-testid="profile-cashapp-btn">
                <div className="text-[10px] text-pfc-gold tracking-[0.4em] mb-1">CashApp</div>
                <div>${handles.cashapp_handle}</div>
              </a>
            )}
            {handles.paypal_url && (
              <a href={handles.paypal_url} target="_blank" rel="noreferrer" className="block px-5 py-4 border border-pfc-gold/30 bg-pfc-gold/5 text-center font-sub uppercase tracking-[0.2em] text-xs text-white hover:bg-pfc-gold hover:text-ink-900 transition" data-testid="profile-paypal-btn">
                <div className="text-[10px] text-pfc-gold tracking-[0.4em] mb-1">PayPal</div>
                <div>{handles.paypal_handle}</div>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ListingTile({ listing, sellerName }) {
  const [showBuy, setShowBuy] = useState(false);
  return (
    <>
      <div className="border border-white/10 bg-ink-800/40 p-4 flex gap-3" data-testid={`profile-listing-${listing.listing_id}`}>
        <div className="w-20 h-20 bg-white/5 shrink-0">
          {listing.image ? <img src={listing.image} alt={listing.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-white/30"><ShoppingBag size={20} /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-pfc-gold font-sub">{listing.listing_type}</div>
          <div className="font-sub text-white truncate">{listing.title}</div>
          <div className="text-white/60 text-xs mt-1 line-clamp-2">{listing.description}</div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-pfc-gold font-display text-lg">{listing.price > 0 ? `$${Number(listing.price).toFixed(2)}` : "Inquiry"}</span>
            <button
              onClick={() => {
                if (listing.external_url) window.open(listing.external_url, "_blank");
                else setShowBuy(true);
              }}
              className="px-3 py-1.5 bg-pfc-gold text-ink-900 text-[11px] uppercase tracking-[0.2em] font-sub hover:bg-pfc-gold-bright"
              data-testid={`buy-listing-${listing.listing_id}`}
            >
              {listing.external_url ? "Buy" : listing.price > 0 ? "Buy Now" : "Inquire"}
            </button>
          </div>
        </div>
      </div>
      {showBuy && <ListingCheckoutModal listing={listing} sellerName={sellerName} onClose={() => setShowBuy(false)} />}
    </>
  );
}

function ListingCheckoutModal({ listing, sellerName, onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", note: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/listings/${listing.listing_id}/checkout`, { ...form, origin_url: window.location.origin });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.ok) {
        alert(`Inquiry sent to ${sellerName}. They'll be in touch.`);
        onClose();
      }
    } catch {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="listing-checkout-modal">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-md w-full p-7">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-xl">{listing.price > 0 ? "Buy" : "Inquire"} · {listing.title}</h3>
          <button onClick={onClose} className="text-white/60 text-sm">Close</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="lcm-name" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lcm-email" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
          <textarea placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="lcm-note" rows={3} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none resize-y" />
          <button type="submit" disabled={loading} data-testid="lcm-submit" className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright disabled:opacity-50">
            {loading ? "…" : listing.price > 0 ? `Pay $${Number(listing.price).toFixed(2)} →` : "Send Inquiry"}
          </button>
        </form>
      </div>
    </div>
  );
}
